import { FormControl, FormControlProps, FormErrorMessage, FormHelperText, FormLabel } from '@chakra-ui/react';
import { FieldPath, FieldPathValue, FieldValues, Validate, useController, useFormContext } from 'react-hook-form';
import { Loader } from '../Loader';
import { RenderOption, Select, SelectOption } from '../Select';
import { useFormErrorMessage } from './useFormErrorMessage';

export type FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TOption extends SelectOption<FieldPathValue<TFieldValues, TFieldName>> = SelectOption<
    FieldPathValue<TFieldValues, TFieldName>
  >,
  IsMulti extends boolean = boolean,
> = FormControlProps & {
  name: TFieldName;
  label: string;
  options: TOption[];
  description?: React.ReactNode;
  placeholder?: string;
  isRequired?: boolean;
  validate?: Validate<FieldPathValue<TFieldValues, TFieldName>, TFieldValues>;
  isLoading?: boolean;
  isClearable?: boolean;
  isMulti?: IsMulti;
  ignoreValueCase?: boolean;
  renderOption?: RenderOption<TOption>;
};

export function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TOption extends SelectOption<FieldPathValue<TFieldValues, TFieldName>> = SelectOption<
    FieldPathValue<TFieldValues, TFieldName>
  >,
  IsMulti extends boolean = boolean,
>({
  name,
  label,
  options,
  description,
  placeholder,
  isRequired,
  validate,
  autoFocus,
  isLoading,
  isClearable,
  isMulti,
  ignoreValueCase,
  size,
  renderOption,
  ...rest
}: FormSelectProps<TFieldValues, TFieldName, TOption, IsMulti>) {
  const {
    control,
    formState: { errors },
  } = useFormContext<TFieldValues>();
  const { field } = useController({ name, control, rules: { required: isRequired && !validate, validate } });

  const errorMessage = useFormErrorMessage(name, label, errors);

  return (
    <FormControl isRequired={isRequired} isInvalid={!!errors[name]} position="relative" {...rest}>
      <Loader isLoading={isLoading || false} />
      <FormLabel size={size}>{label}</FormLabel>
      <Select
        autoFocus={autoFocus}
        placeholder={placeholder}
        options={options}
        isClearable={isClearable}
        isMulti={isMulti}
        ignoreValueCase={ignoreValueCase}
        size={size}
        renderOption={renderOption}
        {...field}
      />
      <FormErrorMessage>{errorMessage}</FormErrorMessage>
      <FormHelperText>{description}</FormHelperText>
    </FormControl>
  );
}
